import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  FileText,
  FolderOpen,
  Settings,
  ClipboardList,
  LogOut,
  Shield,
  ExternalLink,
} from "lucide-react";

const menuItems = [
  { titleKey: "admin.common.dashboard", url: "/admin", icon: LayoutDashboard },
  { titleKey: "admin.common.leads", url: "/admin/leads", icon: Users },
  { titleKey: "admin.common.caseStudies", url: "/admin/case-studies", icon: FileText },
  { titleKey: "admin.common.resources", url: "/admin/resources", icon: FolderOpen },
  { titleKey: "admin.common.auditLog", url: "/admin/audit-log", icon: ClipboardList },
  { titleKey: "admin.common.settings", url: "/admin/settings", icon: Settings },
];

export function AdminSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, roles } = useAuth();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const isActive = (url: string) => {
    if (url === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(url);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b">
        <div className="flex items-center gap-2 px-2 py-3">
          <Shield className="h-6 w-6 text-accent flex-shrink-0" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-semibold text-sm">VisExperience</span>
              <span className="text-xs text-muted-foreground">{t('admin.sidebar.adminPortal')}</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('admin.sidebar.management')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.titleKey}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={t(item.titleKey)}
                  >
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{t(item.titleKey)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>{t('admin.sidebar.quickLinks')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('admin.sidebar.viewWebsite')}>
                  <a href="/" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    <span>{t('admin.sidebar.viewWebsite')}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <div className="p-2">
          {!collapsed && user && (
            <div className="mb-2 px-2">
              <p className="text-xs font-medium truncate">{user.email}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {roles[0] || t('admin.sidebar.defaultRole')}
              </p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4 mr-2" />
            {!collapsed && t('admin.common.signOut')}
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
